import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  format,
  getHours,
  isSameDay,
  parse,
  setDate,
  setHours,
  startOfToday,
} from 'date-fns'
import { type Locale } from 'invictus-sdk-typescript'

import { HUMAN_DATE_FORMAT, ISO_LOCAL_DATE_FORMAT } from '@Constants/date'
import { MIN_DISPLAYED_TIME } from '@DS/components/components/datePicker/DatePicker.constants'
import { type StepHoursMode } from '@DS/components/components/datePicker/DatePicker.types'
import { type DropdownOption } from '@DS/components/forms/dropdown/Dropdown.types'
import { hasHumanDateFormat, hasLocalDateFormat, hasLocalDateTimeFormat } from '@Utils/date'

import { dateFnsLocale } from './locale'

export const specificHourFormats: Record<Locale, string> = {
  fr: "HH'h'", // 06h
  en: 'h\u00A0a', // 6 AM
  de: 'HH:00', // 06:00
  es: "HH'H'", // 06H
  it: "HH\u00A0'h'", // 06 h
}

export const specificHourMinutesFormats: Record<Locale, string> = {
  fr: "HH'h'mm", // 06h30
  en: 'h:mm\u00A0a', // 6:30 AM
  de: 'HH:mm', // 06:30
  es: "HH'H'mm", // 06H30
  it: "HH\u00A0'h'\u00A0mm", // 06 h 30
}

export const generateHours = (
  startHour = MIN_DISPLAYED_TIME,
  stepHoursMode: StepHoursMode = 2,
  locale: Locale = 'fr',
): DropdownOption[] => {
  const result = []
  const timeFormat = specificHourFormats[locale]
  const baseDate = startOfToday()

  if (stepHoursMode === 1) {
    for (let hour = startHour; hour < 24; hour++) {
      const formattedHour = format(setHours(baseDate, hour), timeFormat, { locale: dateFnsLocale[locale] })
      result.push({ value: `${hour}`, label: formattedHour })
    }

    return result
  }

  // Mode 2h
  for (let i = 0; i < 10; i++) {
    const hour = stepHoursMode * i + MIN_DISPLAYED_TIME

    if (hour >= startHour) {
      const formattedHour = format(setHours(baseDate, hour), timeFormat, { locale: dateFnsLocale[locale] })
      result.push({
        value: `${hour}`,
        label: formattedHour,
      })
    }
  }

  return result
}

export const getDateHourWithDefault = ({
  date,
  defaultValue = '6',
  stepHoursMode,
}: {
  date?: Date
  defaultValue?: string
  stepHoursMode: StepHoursMode
}): string => {
  if (date) {
    const hours = getHours(date)

    return `${hours - (hours % stepHoursMode)}`
  }

  return defaultValue
}

export const transformDate = (dateStr: string): Date => {
  if (hasLocalDateFormat(dateStr)) {
    return parse(dateStr, ISO_LOCAL_DATE_FORMAT, new Date())
  }

  if (hasHumanDateFormat(dateStr)) {
    return parse(dateStr, HUMAN_DATE_FORMAT, new Date())
  }

  if (hasLocalDateTimeFormat(dateStr)) {
    return new Date(dateStr)
  }

  return new Date('invalid date')
}

export const isDateOutsideRange = (date: Date, minDate: Date, maxDate: Date | undefined): boolean =>
  differenceInCalendarDays(date, minDate) < 0 || (!!maxDate && differenceInCalendarDays(date, maxDate) > 0)

export const isDateOutsideRestrictedList = (date: Date, restrictedDatesList: Date[] | undefined): boolean =>
  !!restrictedDatesList && restrictedDatesList.find((restrictedDate) => isSameDay(date, restrictedDate)) === undefined

export const getDefaultMaxDate = (): Date => addDays(setDate(addMonths(new Date(), 12), 1), -1)
